// Port of TrailBivvyKit/Sources/GPX/GPXModels.swift (the GPX value types; `GPXError` lives in errors.ts).
//
// Swift structs become interfaces; their computed properties become free functions named `gpx<Type><Property>`.
// Optional Swift properties are optional here and are left out (never `null`) when absent.

import type { EpochMs, GeoCoordinate, RoutePoint } from '../core/models';

/**
 * A GPX point: a standalone waypoint (`<wpt>`), a route point (`<rtept>`) or a track point (`<trkpt>`).
 *
 * Only the fields TrailBivvy uses are kept; other GPX children (`<link>`, `<fix>`, `<hdop>`, `<extensions>`…) are
 * ignored when parsing and never written.
 */
export interface GPXWaypoint {
  /** WGS84 position. Always valid (latitude -90…90, longitude -180…180) when produced by `GPXParser`. */
  coordinate: GeoCoordinate;
  /** Elevation in metres above sea level (`<ele>`). */
  elevation?: number;
  /** Timestamp (`<time>`), an absolute instant (GPX times are UTC). */
  time?: EpochMs;
  /** Name (`<name>`). */
  name?: string;
  /** Comment (`<cmt>`). */
  comment?: string;
  /** Description (`<desc>`). */
  description?: string;
  /** Symbol name (`<sym>`), e.g. a Garmin symbol such as "Flag, Blue". */
  symbol?: string;
  /** Free-form classification (`<type>`). */
  type?: string;
}

/** A continuous span of track points (`<trkseg>`). A new segment usually starts after a GPS fix loss or a pause. */
export interface GPXTrackSegment {
  /** Ordered track points. */
  points: GPXWaypoint[];
}

/** A track (`<trk>`): an ordered list of segments, typically a recorded or planned line. */
export interface GPXTrack {
  /** Name (`<name>`). */
  name?: string;
  /** Description (`<desc>`). */
  description?: string;
  /** Free-form classification (`<type>`), e.g. "hiking". */
  type?: string;
  /** Ordered segments. May contain empty segments when the source file does. */
  segments: GPXTrackSegment[];
}

/** A route (`<rte>`): an ordered list of route points, typically sparse turn points of a planned route. */
export interface GPXRoute {
  /** Name (`<name>`). */
  name?: string;
  /** Description (`<desc>`). */
  description?: string;
  /** Ordered route points. */
  points: GPXWaypoint[];
}

/** File-level metadata. Read from `<metadata>` (GPX 1.1) or from the root element's children (GPX 1.0). */
export interface GPXMetadata {
  /** Name of the file's content (`<name>`). */
  name?: string;
  /** Description (`<desc>`). */
  description?: string;
  /** Author name (`<author><name>` in GPX 1.1, `<author>` in GPX 1.0). */
  author?: string;
  /** Creation time of the file, or activity start for many recording apps (`<time>`). */
  time?: EpochMs;
}

/** An in-memory GPX document (GPX 1.0 or 1.1 on input, written as GPX 1.1). */
export interface GPXDocument {
  /** The `creator` attribute of the root element (the application that produced the file). */
  creator?: string;
  /** File-level metadata. */
  metadata: GPXMetadata;
  /** Standalone waypoints (`<wpt>`). */
  waypoints: GPXWaypoint[];
  /** Routes (`<rte>`). */
  routes: GPXRoute[];
  /** Tracks (`<trk>`). */
  tracks: GPXTrack[];
}

/**
 * A document with Swift's `GPXDocument.init` defaults (empty metadata and lists) for the fields not given.
 * Fields given as `undefined` are left out.
 */
export function makeGPXDocument(fields: Partial<GPXDocument> = {}): GPXDocument {
  const document: GPXDocument = {
    metadata: fields.metadata ?? {},
    waypoints: fields.waypoints ?? [],
    routes: fields.routes ?? [],
    tracks: fields.tracks ?? [],
  };
  if (fields.creator !== undefined) document.creator = fields.creator;
  return document;
}

/** The point as a domain `RoutePoint` (coordinate + elevation). Swift `GPXWaypoint.routePoint`. */
export function gpxWaypointRoutePoint(waypoint: GPXWaypoint): RoutePoint {
  const coordinate = { latitude: waypoint.coordinate.latitude, longitude: waypoint.coordinate.longitude };
  return waypoint.elevation === undefined ? { coordinate } : { coordinate, elevation: waypoint.elevation };
}

/** All points of all segments, in order. Swift `GPXTrack.allPoints`. */
export function gpxTrackAllPoints(track: GPXTrack): GPXWaypoint[] {
  return track.segments.flatMap((segment) => segment.points);
}

/** True when no metadata field is set. Swift `GPXMetadata.isEmpty`. */
export function gpxMetadataIsEmpty(metadata: GPXMetadata): boolean {
  return (
    metadata.name === undefined &&
    metadata.description === undefined &&
    metadata.author === undefined &&
    metadata.time === undefined
  );
}

/**
 * True when the document contains no point at all: no waypoint, no route point and no track point.
 * (Empty routes, tracks or segments do not count as content.) Swift `GPXDocument.isEmpty`.
 */
export function gpxDocumentIsEmpty(document: GPXDocument): boolean {
  return (
    document.waypoints.length === 0 &&
    document.routes.every((route) => route.points.length === 0) &&
    document.tracks.every((track) => track.segments.every((segment) => segment.points.length === 0))
  );
}

/** Every track point of every track and segment, in document order. Swift `GPXDocument.allTrackPoints`. */
export function gpxDocumentAllTrackPoints(document: GPXDocument): GPXWaypoint[] {
  return document.tracks.flatMap(gpxTrackAllPoints);
}
